import { ACCESS_KEY, urlAPI } from '../../../urls'

export interface OrderRelatedServicePatch {
    Status?: string
    Notify?: boolean
    ScheduledAt?: string | null
    updatedAt?: string
}

export interface UpdateOrderRelatedServiceArgs {
    order: any
    serviceId: string
    itemIndex?: number
    patch: OrderRelatedServicePatch
}

export interface RootOrderServiceStatusPatch {
    ServiceStatus?: string
    Statuses?: string[]
    Notify?: boolean
    ScheduledAt?: string | null
    updatedAt?: string
}

export interface UpdateRootOrderServiceStatusArgs {
    order: any
    patch: RootOrderServiceStatusPatch
}

export interface FindingTenantOrderDatesPatch {
    ServiceStartDate: string
    ServiceEndDate: string
}

export const BLOCKED_RELATED_PATCH_FIELDS = ['DownloadUrls', 'CompletedAt', 'ReadyAt', 'StartDate', 'StartTime'] as const
export const BLOCKED_ORDER_BODY_FIELDS = ['Service', 'ProductMetadata', 'StartDate', 'StartTime'] as const
const ALLOWED_RELATED_PATCH_FIELDS = ['Status', 'Notify', 'ScheduledAt', 'updatedAt'] as const
const ALLOWED_ROOT_PATCH_FIELDS = ['ServiceStatus', 'Statuses', 'Notify', 'ScheduledAt', 'updatedAt'] as const

const normalizeServiceToken = (value: unknown): string => {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/^services\./, '')
        .replace(/[\s-]+/g, '_')
}

const getRelatedServiceId = (item: any): string => {
    const raw =
        item?.serviceId ??
        item?.ServiceID ??
        item?.ServiceId ??
        (typeof item?.id === 'string' ? item.id : undefined)

    return normalizeServiceToken(raw)
}

export const hasLegacyNestedRelatedServices = (order: any): boolean => {
    if (Array.isArray(order?.RelatedServices) && order.RelatedServices.length > 0) return false
    return Array.isArray(order?.Service) && order.Service.some((service: any) => Array.isArray(service?.RelatedServices) && service.RelatedServices.length > 0)
}

export const sanitizeRelatedServicePatch = (patch: Record<string, any>): OrderRelatedServicePatch => {
    const unsafeField = BLOCKED_RELATED_PATCH_FIELDS.find((field) => Object.prototype.hasOwnProperty.call(patch, field))
    if (unsafeField != null) throw new Error(`Blocked related-service patch field: ${unsafeField}`)

    return ALLOWED_RELATED_PATCH_FIELDS.reduce<OrderRelatedServicePatch>((acc, field) => {
        if (Object.prototype.hasOwnProperty.call(patch, field)) {
            acc[field] = patch[field]
        }
        return acc
    }, {})
}

export const sanitizeRootOrderServiceStatusPatch = (patch: Record<string, any>): RootOrderServiceStatusPatch => {
    const unsafeField = [...BLOCKED_RELATED_PATCH_FIELDS, ...BLOCKED_ORDER_BODY_FIELDS, 'RelatedServices'].find((field) => Object.prototype.hasOwnProperty.call(patch, field))
    if (unsafeField != null) throw new Error(`Blocked root-order patch field: ${unsafeField}`)

    return ALLOWED_ROOT_PATCH_FIELDS.reduce<RootOrderServiceStatusPatch>((acc, field) => {
        if (Object.prototype.hasOwnProperty.call(patch, field)) {
            acc[field] = patch[field]
        }
        return acc
    }, {})
}

export const buildOrderRelatedServiceUpdateBody = ({ order, serviceId, itemIndex, patch }: UpdateOrderRelatedServiceArgs): { RelatedServices: any[] } => {
    if (!Array.isArray(order?.RelatedServices)) {
        throw new Error(hasLegacyNestedRelatedServices(order)
            ? 'Legacy nested related services detected. Migration required.'
            : 'Top-level RelatedServices are missing.')
    }

    const safePatch = sanitizeRelatedServicePatch(patch)
    const nextRelatedServices = order.RelatedServices.map((item: any, index: number) => {
        if (itemIndex != null ? index !== itemIndex : getRelatedServiceId(item) !== serviceId) return item
        return { ...item, ...safePatch }
    })

    const didUpdate = itemIndex != null
        ? itemIndex >= 0 && itemIndex < order.RelatedServices.length && getRelatedServiceId(order.RelatedServices[itemIndex]) === serviceId
        : nextRelatedServices.some((item: any, index: number) => getRelatedServiceId(item) === serviceId && item !== order.RelatedServices[index])
    if (!didUpdate) {
        throw new Error(`Related service "${serviceId}" not found on top-level order.RelatedServices.`)
    }

    return { RelatedServices: nextRelatedServices }
}

export const assertSafeOrderRelatedServiceBody = (body: Record<string, any>): void => {
    const unsafeBodyField = BLOCKED_ORDER_BODY_FIELDS.find((field) => Object.prototype.hasOwnProperty.call(body, field))
    if (unsafeBodyField != null) throw new Error(`Blocked order update body field: ${unsafeBodyField}`)
    if (!Array.isArray(body.RelatedServices)) throw new Error('Related service update must send body.RelatedServices.')
}

export const buildRootOrderServiceStatusUpdateBody = ({ order, patch }: UpdateRootOrderServiceStatusArgs): RootOrderServiceStatusPatch => {
    const safePatch = sanitizeRootOrderServiceStatusPatch(patch)
    if (Object.keys(safePatch).length === 0) throw new Error('Root order status update body is empty.')

    return safePatch
}

export const assertSafeRootOrderServiceStatusBody = (body: Record<string, any>): void => {
    const unsafeBodyField = [...BLOCKED_RELATED_PATCH_FIELDS, ...BLOCKED_ORDER_BODY_FIELDS, 'RelatedServices'].find((field) => Object.prototype.hasOwnProperty.call(body, field))
    if (unsafeBodyField != null) throw new Error(`Blocked root-order update body field: ${unsafeBodyField}`)
    const unsupportedField = Object.keys(body).find((field) => !ALLOWED_ROOT_PATCH_FIELDS.includes(field as typeof ALLOWED_ROOT_PATCH_FIELDS[number]))
    if (unsupportedField != null) throw new Error(`Unsupported root-order update body field: ${unsupportedField}`)
}

export const buildFindingTenantOrderDatesUpdateBody = (patch: FindingTenantOrderDatesPatch): FindingTenantOrderDatesPatch => {
    const dates = {
        ServiceStartDate: String(patch?.ServiceStartDate ?? '').trim(),
        ServiceEndDate: String(patch?.ServiceEndDate ?? '').trim(),
    }
    if (dates.ServiceStartDate.length === 0 || dates.ServiceEndDate.length === 0) {
        throw new Error('Finding Tenant order dates are required.')
    }
    return dates
}

export const assertSafeFindingTenantOrderDatesBody = (body: Record<string, any>): void => {
    const allowedFields = ['ServiceStartDate', 'ServiceEndDate']
    const unsupportedField = Object.keys(body).find((field) => !allowedFields.includes(field))
    if (unsupportedField != null) throw new Error(`Unsupported Finding Tenant date field: ${unsupportedField}`)
    if (typeof body.ServiceStartDate !== 'string' || typeof body.ServiceEndDate !== 'string') {
        throw new Error('Finding Tenant order dates must be strings.')
    }
}

export const updateOrderRelatedService = async ({ order, serviceId, itemIndex, patch }: UpdateOrderRelatedServiceArgs): Promise<any> => {
    const orderId = Number(order?.id)
    if (!Number.isFinite(orderId) || orderId <= 0) throw new Error('Order id is missing.')

    const body = buildOrderRelatedServiceUpdateBody({ order, serviceId, itemIndex, patch })
    assertSafeOrderRelatedServiceBody(body)

    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const response = await fetch(urlAPI, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            method: 'PUT',
            collection: 'orders',
            id: orderId,
            body,
        }),
    })
    const json = await response.json()
    if (!json?.success || json?.response?.error) {
        throw new Error(JSON.stringify(json?.response?.error ?? json))
    }
    return json.response
}

export const updateRootOrderServiceStatus = async ({ order, patch }: UpdateRootOrderServiceStatusArgs): Promise<any> => {
    const orderId = Number(order?.id)
    if (!Number.isFinite(orderId) || orderId <= 0) throw new Error('Order id is missing.')

    const body = buildRootOrderServiceStatusUpdateBody({ order, patch })
    assertSafeRootOrderServiceStatusBody(body)

    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const response = await fetch(urlAPI, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            method: 'PUT',
            collection: 'orders',
            id: orderId,
            body,
        }),
    })
    const json = await response.json()
    if (!json?.success || json?.response?.error) {
        throw new Error(JSON.stringify(json?.response?.error ?? json))
    }
    return json.response
}

export const updateFindingTenantOrderDates = async ({ order, patch }: { order: any, patch: FindingTenantOrderDatesPatch }): Promise<any> => {
    const orderId = Number(order?.id)
    if (!Number.isFinite(orderId) || orderId <= 0) throw new Error('Order id is missing.')

    const body = buildFindingTenantOrderDatesUpdateBody(patch)
    assertSafeFindingTenantOrderDatesBody(body)

    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const response = await fetch(urlAPI, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            method: 'PUT',
            collection: 'orders',
            id: orderId,
            body,
        }),
    })
    const json = await response.json()
    if (!json?.success || json?.response?.error) {
        throw new Error(JSON.stringify(json?.response?.error ?? json))
    }
    return json.response
}
